/**
 * Utility functions for converting UTC dates to local timezone for display
 * This ensures date integrity when displaying dates to users
 */

/**
 * Convert a date string to Date object for display
 * Since database uses date type (YYYY-MM-DD), we just parse it as local date
 * @param {Date|string} dateValue - The date to convert (can be Date object or YYYY-MM-DD string)
 * @returns {Date} - A date object representing the calendar date
 */
export const toLocalDate = (dateValue) => {
  if (!dateValue) return null;

  // If already a Date object, return as-is
  if (dateValue instanceof Date) {
    return dateValue;
  }

  // If it's a YYYY-MM-DD string, parse it as local date
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // If it's an ISO string with T, strip the Z/offset to treat it as local wall time
  // This is because our health logs save local time as a string without timezone, 
  // which Supabase may mark as UTC with a 'Z' on retrieval.
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    const clean = dateValue.replace(/Z$|[+-]\d{2}(:?\d{2})?(:?\d{2})?$/, '');
    const date = new Date(clean);
    if (!isNaN(date.getTime())) return date;
  }

  // Otherwise, try to parse as Date
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return null;

  return date;
};

/**
 * Format a date for display in local timezone
 * Since database uses date type (YYYY-MM-DD), dates are already simple date strings
 * @param {Date|string} dateValue - The date to format (can be Date object or YYYY-MM-DD string)
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string in local timezone
 */
export const formatLocalDate = (dateValue, options = {}) => {
  if (!dateValue) return '';

  const localDate = toLocalDate(dateValue);
  if (!localDate) return '';

  return localDate.toLocaleDateString('en-US', options);
};

/**
 * Format a date for date input fields (YYYY-MM-DD)
 * Since database uses date type, dates are already in YYYY-MM-DD format
 * @param {Date|string} dateValue - The date to format (can be Date object or YYYY-MM-DD string)
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const formatLocalDateForInput = (dateValue) => {
  if (!dateValue) return '';

  // If already in YYYY-MM-DD format, return as-is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // If it's a Date object, extract YYYY-MM-DD
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date in local timezone for display
 * @returns {Date} - Today's date in local timezone
 */
export const getTodayLocal = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

/**
 * Convert a date to YYYY-MM-DD format for database storage (date type)
 * Since database uses date type (not timestamptz), we just return YYYY-MM-DD string
 * @param {Date|string} dateInput - The date to format (can be Date object or YYYY-MM-DD string)
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const formatDateForStorage = (dateInput) => {
  if (!dateInput) return null;

  // If already in YYYY-MM-DD format, return as-is
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }

  // If it's a Date object, extract YYYY-MM-DD
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * @deprecated Use formatDateForStorage instead - database now uses date type, not timestamptz
 * Kept for backward compatibility during migration
 */
export const localToUTC = (localDate) => {
  // For date type, just return the formatted date string
  return formatDateForStorage(localDate);
};

